/* Modale de rendu d'un devoir : dépôt de photos par glisser-déposer ou sélection, */
/* aperçu, puis upload (URL signée) + soumission. */

import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@base-ui/react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api } from '../lib/api';
import { cn } from '@/lib/utils';

interface Picked {
  file: File;
  url: string;
}

export interface SubmitDevoirModalProps {
  studentId: string;
  assignmentId: string;
  title: string;
  onClose: () => void;
  onDone: () => void;
}

const dropzoneBase = "flex flex-col items-center gap-[6px] p-[28px_16px] border-[2px] border-dashed border-border rounded-card text-muted-foreground text-center cursor-pointer hover:border-muted-foreground";

export default function SubmitDevoirModal({ studentId, assignmentId, title, onClose, onDone }: SubmitDevoirModalProps) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => picked.forEach((p) => URL.revokeObjectURL(p.url)), [picked]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = Array.from(list)
      .filter((f) => f.type.startsWith('image/'))
      .map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPicked((prev) => [...prev, ...next]);
  };

  const removeAt = (i: number) =>
    setPicked((prev) => {
      const target = prev[i];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, j) => j !== i);
    });

  const submit = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const assets: Array<{ storagePath: string; contentType: string; byteSize: number }> = [];
      for (const { file } of picked) {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const { path, signed_url } = await api.parentOrTutor.student.uploadUrl.mutate({ studentId, assignmentId, ext });
        const put = await fetch(signed_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!put.ok) throw new Error("Échec de l'envoi d'une photo.");
        assets.push({ storagePath: path, contentType: file.type || 'application/octet-stream', byteSize: file.size });
      }
      await api.parentOrTutor.student.submit.mutate({ studentId, assignmentId, assets });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-[rgba(11,42,74,0.4)]">
          <Dialog.Popup className="flex flex-col gap-[14px] w-full max-w-[440px] max-h-[90vh] overflow-y-auto bg-card rounded-card shadow-card p-5" aria-label={`Rendre : ${title}`}>
            <h2 className="m-0 text-[18px] text-foreground">Rendre — {title}</h2>

            <div
              className={cn(dropzoneBase, dragging && "border-primary bg-[color-mix(in_srgb,var(--naia-blue)_8%,transparent)]")}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            >
              <div className="text-[30px]">📷</div>
              <p className="m-0 text-[14px]">
                Glisse-dépose les photos ici
                <br />
                ou <strong>clique pour choisir</strong>
              </p>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {picked.length > 0 && (
              <div className="flex flex-col gap-2">
                {picked.map((p, i) => (
                  <div key={p.url} className="flex items-center gap-[10px] p-[6px] border border-border rounded-[10px]">
                    <img className="w-[44px] h-[44px] object-cover rounded-[8px] flex-none" src={p.url} alt="" />
                    <span className="flex-1 text-[13px] text-foreground overflow-hidden text-ellipsis whitespace-nowrap">{p.file.name}</span>
                    <button type="button" className="min-w-[44px] min-h-[44px] border-none bg-transparent text-muted-foreground text-[22px] leading-none cursor-pointer" onClick={() => removeAt(i)} aria-label="Retirer" disabled={busy}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {error && <Alert>{error}</Alert>}

            <div className="flex justify-end gap-[10px]">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Annuler</Button>
              <Button onClick={() => void submit()} disabled={busy || picked.length === 0}>
                {busy ? "Envoi…" : `Rendre ${picked.length || ''} photo${picked.length > 1 ? 's' : ''}`.trim()}
              </Button>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
